import React, { useContext } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { Form } from 'formik';
import { AlertMessage } from '@eposnow/ui-core';
import { UserContext } from '../../context/UserContext';
import { MFAErrors, VerificationCodeDetails } from '../../types';
import FormikNumberField from '../../components/formikComponents/FormikNumberField';
import { REACT_APP_LINK_SUPPORT_KEY } from '../../localStorageKeys';

interface CodeVerificationModalProps {
  open: boolean;
  verificationCodeDetails: VerificationCodeDetails;
  handleClose: () => void;
  handleSubmit: () => void;
  errors: MFAErrors;
  clearError: (errorKey: string) => void;
  isLoadingUpdate: boolean;
}

const CodeVerificationModal = ({
  open,
  verificationCodeDetails,
  handleClose,
  handleSubmit,
  errors,
  clearError,
  isLoadingUpdate,
}: CodeVerificationModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useContext(UserContext);

  return (
    <Dialog
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        '& .MuiDialogContent-root': {
          paddingX: '1.5rem',
          paddingY: 0,
        },
        '& .MuiDialogActions-root': {
          padding: '1rem 1.5rem',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        component={Box}
      >
        <Typography variant="h2">{t('screens.mfa.verificationCode.title')}</Typography>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Form onSubmit={handleSubmit}>
        <DialogContent>
          <Typography sx={{ marginBottom: '1.5rem' }}>
            <Trans
              i18nKey="screens.mfa.verificationCode.description"
              values={{
                channel: verificationCodeDetails?.channel,
                contactInfo: verificationCodeDetails?.contactInfo,
              }}
            />
          </Typography>
          {errors?.verificationCodeAlertError && (
            <AlertMessage
              type="error"
              theme={theme}
              locale={user.locale}
              styles={{ marginBottom: '1rem' }}
            >
              {errors?.verificationCodeAlertError}
            </AlertMessage>
          )}
          <FormikNumberField
            label={t('screens.mfa.verificationCode.input')}
            id="verificationCode"
            formName="mfa"
            objectId={user.id}
            maxValue={999999}
            minValue={0}
            type="tel"
            allowLeadingZeros
            isAllowed={(values) => values?.value.length <= 6}
            sx={{
              marginBottom: 0,
              maxWidth: { xs: '100%', md: '50%' },
              '& .MuiInputBase-input': {
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.125rem' },
                letterSpacing: { xs: '3px', sm: '4px' },
              },
            }}
            helperText=" "
            decimalScale={0}
            required
            name="verificationCode"
            error={errors?.verificationCode}
            clearError={clearError}
          />

          <Typography variant="body2" sx={{ marginTop: '1rem' }}>
            {`${t('screens.mfa.verificationCode.checkSpam')} `}
            <Trans i18nKey="screens.mfa.verificationCode.contactSupport">
              {t('screens.mfa.verificationCode.contactSupport')}
              <Link
                href={`${localStorage.getItem(REACT_APP_LINK_SUPPORT_KEY)}s/?language=${
                  user.locale?.split('-')?.[0] || 'en'
                }`}
                target="_blank"
              />
            </Trans>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button autoFocus color="primary" variant="contained" type="submit" disableElevation>
            {isLoadingUpdate && (
              <>
                <CircularProgress
                  aria-label={t('screens.mfa.verificationCode.updating')}
                  aria-live="polite"
                  aria-busy={isLoadingUpdate}
                  data-testid="loading-icon"
                  color="inherit"
                  size="20px"
                  sx={{ marginRight: '8px' }}
                />
                {t('actions.saving')}
              </>
            )}
            {!isLoadingUpdate && t('actions.save')}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default CodeVerificationModal;
